import {
  DEFAULT_APPLE_TOUCH_ICON,
  DEFAULT_FAVICON,
  revokePwaIconBlobs,
} from "./pwaIcon";
import { getSubdomainForApi, isClinicPortal } from "./portalHost";

const MANIFEST_BY_PREFIX = [
  { prefix: "/admin", href: "/manifest-platform.json", role: "platform" },
  { prefix: "/manage/app", href: "/manifest-admin.json", role: "admin" },
  { prefix: "/staff/app", href: "/manifest-staff.json", role: "staff" },
  { prefix: "/app", href: "/manifest-client.json", role: "client" },
  { prefix: "/manage", href: "/manifest-admin.json", role: "admin" },
  { prefix: "/staff", href: "/manifest-staff.json", role: "staff" },
  { prefix: "/login", href: "/manifest-client.json", role: "client" },
  { prefix: "/", href: "/manifest-client.json", role: "client" },
];

const PORTAL_MANIFEST_ROLE_BY_PREFIX = [
  { prefix: "/manage", role: "admin" },
  { prefix: "/staff", role: "staff" },
  { prefix: "/app", role: "client" },
  { prefix: "/login", role: "client" },
];

let applyGeneration = 0;

function matchManifest(pathname) {
  const path = pathname.replace(/\/$/, "") || "/";
  return (
    MANIFEST_BY_PREFIX.find((item) => {
      if (item.prefix === "/") return path === "/";
      return path === item.prefix || path.startsWith(`${item.prefix}/`);
    }) || MANIFEST_BY_PREFIX.find((item) => item.prefix === "/app")
  );
}

function matchPortalRole(pathname) {
  const path = pathname.replace(/\/$/, "") || "/";
  return (
    PORTAL_MANIFEST_ROLE_BY_PREFIX.find(
      (item) => path === item.prefix || path.startsWith(`${item.prefix}/`),
    )?.role || "client"
  );
}

function ensureLink(rel, attrs = {}) {
  const selector = attrs.sizes
    ? `link[rel="${rel}"][sizes="${attrs.sizes}"]`
    : `link[rel="${rel}"]`;
  let link = document.querySelector(selector);
  if (!link) {
    link = document.createElement("link");
    link.rel = rel;
    document.head.appendChild(link);
  }
  for (const [key, value] of Object.entries(attrs)) {
    if (value != null) link.setAttribute(key, value);
  }
  return link;
}

function upgradeMixedContentUrl(url) {
  if (
    typeof window !== "undefined" &&
    window.location.protocol === "https:" &&
    /^http:\/\//i.test(url)
  ) {
    return url.replace(/^http:/i, "https:");
  }
  return url;
}

function absoluteUrl(path) {
  if (!path) return null;
  const value = String(path).trim();
  if (!value) return null;
  if (/^(https?:|blob:|data:)/i.test(value)) return upgradeMixedContentUrl(value);
  if (typeof window === "undefined") return value;
  return upgradeMixedContentUrl(new URL(value, window.location.origin).href);
}

function setManifestHref(href) {
  const link = ensureLink("manifest");
  link.setAttribute("href", href);
}

function setAppleTouchIcon(href) {
  const link = ensureLink("apple-touch-icon");
  link.setAttribute("href", absoluteUrl(href) || href);
}

function setFavicon(href) {
  const link = ensureLink("icon", { type: "image/png" });
  link.setAttribute("href", absoluteUrl(href) || href);
}

function setAppleWebAppTitle(title) {
  let meta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
  if (!title) {
    meta?.remove();
    return;
  }
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "apple-mobile-web-app-title");
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", title);
}

function setThemeColor(color) {
  if (!color) return;
  // Чёлкой управляет applyPwaChromeColor — не затираем accent'ом из манифеста
  if (document.documentElement.classList.contains("pwa-chrome-themed")) {
    return;
  }
  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "theme-color");
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", color);
}

function applyDefaultPwaAssets() {
  revokePwaIconBlobs();
  setAppleTouchIcon(DEFAULT_APPLE_TOUCH_ICON);
  setFavicon(DEFAULT_FAVICON);
  setAppleWebAppTitle(null);
}

function portalManifestHref(role) {
  const params = new URLSearchParams({ role });
  const tenant = getSubdomainForApi();
  if (tenant) params.set("tenant", tenant);
  return `/api/public/pwa-manifest?${params.toString()}`;
}

async function applyPortalPwaManifest(pathname, branding = null) {
  const role = matchPortalRole(pathname);
  const href = portalManifestHref(role);
  setManifestHref(href);

  try {
    const metaUrl = href.replace(
      "/api/public/pwa-manifest?",
      "/api/public/pwa-manifest/meta?",
    );
    const res = await fetch(metaUrl, {
      cache: "no-cache",
    });
    if (!res.ok) throw new Error("meta failed");
    const meta = await res.json();
    if (meta.apple_touch_icon) {
      setAppleTouchIcon(meta.apple_touch_icon);
      setFavicon(meta.apple_touch_icon);
    }
    if (meta.short_name) setAppleWebAppTitle(meta.short_name);
    if (meta.theme_color) setThemeColor(meta.theme_color);
  } catch {
    const touchIcon =
      absoluteUrl(branding?.logoSrc) || absoluteUrl(DEFAULT_APPLE_TOUCH_ICON);
    setAppleTouchIcon(touchIcon);
    setFavicon(touchIcon);
    if (branding?.clubName) {
      setAppleWebAppTitle(branding.clubName.slice(0, 20));
    }
    if (branding?.themeColor) setThemeColor(branding.themeColor);
  }
}

/**
 * @param {string} pathname
 * @param {object|null} branding
 */
export async function applyPwaManifest(pathname = window.location.pathname, branding = null) {
  if (typeof document === "undefined") return;

  const generation = ++applyGeneration;
  const match = matchManifest(pathname);

  if (isClinicPortal()) {
    await applyPortalPwaManifest(pathname, branding);
    if (generation !== applyGeneration) return;
    return;
  }

  applyDefaultPwaAssets();
  setManifestHref(match.href);
}
